import { getOidcClient } from '@/services/oidc_client'
import router from '@/router'
import elasticSearchClient from '@/api/elasticSearchClient.js'

/**
 * OpenID Connect flow using oidc-client-js library(@see: https://github.com/IdentityModel/oidc-client-js)
 * @author Martin Besozzi <mbesozzi@identicum.com>
 */

// ---------------------------------------------------------------------------
// OIDC logging helper — all OIDC logs are prefixed with an ISO timestamp
// so that screenshots and console exports have precise timing information.
// ---------------------------------------------------------------------------
function oidcLog (level, ...args) {
	const ts = new Date().toISOString()
	const prefix = `[${ts}] OIDC:`
	if (level === 'error') {
		console.error(prefix, ...args)
	} else if (level === 'warn') {
		console.warn(prefix, ...args)
	} else {
		console.log(prefix, ...args)
	}
}

const defaultState = {
	user: {},
	isOidcEventsHandled: false
}

let oidcClient = getOidcClient()

// Toast ID to prevent duplicate toasts
let sessionExpiredToastId = null

// Module-level flag tracking whether the OIDC session has been invalidated
// (e.g. invalid_grant error). This is kept in sync with
// sessionStorage('oidc_session_invalidated') but avoids async storage lookups
// in hot paths and survives page refreshes via sessionStorage.
let sessionInvalidated = sessionStorage.getItem('oidc_session_invalidated') === 'true'

/**
 * Show a persistent toast warning the user their session has expired
 */
function showSessionExpiredToast () {
	// Prevent showing multiple toasts
	if (sessionExpiredToastId) {
		return
	}
	const nextUrl = window.location.pathname + window.location.search
	const loginUrl = router.resolve({
		name: 'login',
		query: { next_url: nextUrl }
	}).href
	sessionExpiredToastId = window.vm.$Toaster({
		type: 'warning',
		title: 'Session expirée',
		content: `
			Votre session a expiré.<br>
			La validation d'une tâche manuelle est encore possible,<br>
			mais vous ne pourrez pas effectuer d'autres actions nécessitant une
			authentification tant que vous n'aurez pas renouvelé votre session.<br>
			<strong><a href="${loginUrl}">Se reconnecter</a></strong>.
		`
	})
}

/**
 * Mark the OIDC session as invalidated (both in-memory and sessionStorage).
 */
function markSessionInvalidated () {
	sessionInvalidated = true
	sessionStorage.setItem('oidc_session_invalidated', 'true')
}

/**
 * Clear the OIDC session invalidation flag (both in-memory and sessionStorage).
 */
function clearSessionInvalidated () {
	sessionInvalidated = false
	sessionStorage.removeItem('oidc_session_invalidated')
}

const actions = {
	signin: () => {
		if (window.vm.$route.query.next_url) {
			window.localStorage.setItem('next_url', window.vm.$route.query.next_url)
		}
		oidcClient.signinRedirect()
	},
	signinSilent: () => {
		return oidcClient.signinSilent()
	},
	signinRedirectCallback: (context) => {
		context.commit('TOKEN_REQUEST_PENDING')
		oidcClient.signinRedirectCallback()
			.then((response) => {
				context.commit('TOKEN_REQUEST_SUCCESS')
				context.dispatch('setAuthenticationSuccess', response)
					.catch((error) => {
						oidcLog('error', 'setAuthenticationSuccess failed after redirect callback', error.message)
					})
			})
			.catch(function (error) {
				context.commit('TOKEN_REQUEST_FAILURE', error)
				oidcLog('error', 'signinRedirectCallback failed', error)
				router.push('/login')
					.catch((error) => {
						console.log(error.message)
					})
			})
	},
	setAuthenticationSuccess: (context, user) => {
		// Reset session state on successful authentication
		sessionExpiredToastId = null
		clearSessionInvalidated()

		// Restart silent renew in case it was stopped by a previous auth error
		// (stopSilentRenew is called in addSilentRenewError on auth errors to
		// prevent retry loops, but a successful re-login means we have fresh
		// tokens and should resume automatic renewal)
		oidcClient.startSilentRenew()
		oidcLog('info', 'Authentication successful, silent renew (re)started')

		// Check if library is suscribed to OIDC library events
		if (!context.state.isOidcEventsHandled) {
			oidcClient.events.addAccessTokenExpiring(() => {
				if (sessionInvalidated) {
					oidcLog('warn', 'Access token expiring but session is invalidated — cannot renew, token will expire soon')
					return
				}
				oidcLog('info', 'Access token expiring, silent renew will handle renewal automatically')
			})
			oidcClient.events.addAccessTokenExpired(() => {
				if (sessionInvalidated) {
					oidcLog('warn', 'Access token expired (session was already invalidated)')
					showSessionExpiredToast()
					return
				}
				// Access token expired but session wasn't marked invalidated yet.
				// Before giving up, check if another tab renewed (our timers might
				// be stale if a storage event was missed).
				oidcClient.getUser().then(user => {
					if (user && !user.expired) {
						const remaining_seconds = user.expires_at - Math.floor(Date.now() / 1000)
						if (remaining_seconds > 120) {
							oidcLog('info', 'Access token expired event but fresh token found (remaining: ' + remaining_seconds + 's), recovering via multi-tab sync')
							clearSessionInvalidated()
							oidcClient.startSilentRenew()
							return
						}
					}
					// No recovery possible — mark session invalidated to prevent
					// futile signinSilent() calls from getUser() or other code paths
					oidcLog('warn', 'Access token expired, no fresh tokens available — marking session invalidated')
					markSessionInvalidated()
					showSessionExpiredToast()
				}).catch(() => {
					markSessionInvalidated()
					showSessionExpiredToast()
				})
			})
			oidcClient.events.addSilentRenewError((error) => {
				// Silent renew failed — determine severity and act accordingly.
				oidcLog('warn', 'Silent renew error', error)

				const errorMessage = error && error.message ? error.message.toLowerCase() : ''

				// ---- Network errors (transient) ----
				// User may still have a valid session — oidc-client will retry on
				// the next accessTokenExpiring cycle.
				const isNetworkError = errorMessage.includes('network') ||
					errorMessage.includes('fetch') ||
					errorMessage.includes('timeout') ||
					error instanceof TypeError // fetch network errors are TypeErrors

				if (isNetworkError) {
					oidcLog('info', 'Silent renew failed due to network error, will retry automatically on next cycle')
					return
				}

				// ---- Auth errors (invalid_grant, login_required, etc.) ----
				// Could be a genuine session expiration OR a multi-tab race condition:
				// when multiple tabs share the same refresh token via localStorage,
				// one tab consumes it (rotation) and the others get invalid_grant.
				// Before killing the session, re-read the user from localStorage to
				// check if another tab already renewed successfully.
				const isAuthError = errorMessage.includes('invalid_grant') ||
					errorMessage.includes('login_required') ||
					errorMessage.includes('consent_required') ||
					errorMessage.includes('interaction_required')

				if (isAuthError) {
					oidcLog('warn', 'Auth error detected (' + errorMessage + '), checking if another tab already renewed')

					// Re-read user from localStorage — getUser() internally calls
					// _events.load(user) which reinitializes the expiry timers,
					// which is exactly what we want if another tab renewed.
					oidcClient.getUser().then(user => {
						if (user && !user.expired) {
							// Token isn't expired yet, but is it truly FRESH (from another tab)
							// or just the same old token that hasn't expired yet?
							// Since accessTokenExpiring fires 60s before expiry, the old token
							// still has ~60s remaining. A genuinely fresh token from another tab
							// would have a full lifetime remaining (typically 15min-1h).
							// We use 120s as threshold to distinguish the two cases.
							const remaining_seconds = user.expires_at - Math.floor(Date.now() / 1000)
							if (remaining_seconds > 120) {
								// Fresh tokens found — another tab won the renewal race.
								// Our refresh token was stale (consumed by the winning tab).
								// getUser() already reinitialized timers for the new token.
								// Restart silent renew and carry on.
								oidcLog('info', 'Fresh tokens found in storage (expires_at: ' + new Date(user.expires_at * 1000).toISOString() + ', remaining: ' + remaining_seconds + 's) — another tab already renewed, resuming silent renew')
								clearSessionInvalidated()
								oidcClient.startSilentRenew()
								return
							}
							// Token is not expired yet but will be very soon (<= 120s).
							// This is the SAME old token, not a fresh one from another tab.
							oidcLog('warn', 'Token in storage not yet expired but only ' + remaining_seconds + 's remaining — not a fresh renewal, treating as expired')
						}
						// No fresh tokens — session is truly expired
						oidcLog('warn', 'No fresh tokens in storage, stopping automatic silent renew')
						oidcClient.stopSilentRenew()
						markSessionInvalidated()
						oidcLog('warn', 'Session invalidated — refresh token is no longer valid, toast will appear when access token actually expires')
					}).catch(() => {
						// Can't even read user — assume the worst
						oidcClient.stopSilentRenew()
						markSessionInvalidated()
					})
					return
				}

				// ---- Unknown errors ----
				// Could be iframe timeout, popup blocked, etc. Log but don't
				// take drastic action — oidc-client may retry on its own.
				oidcLog('warn', 'Non-critical silent renew error, token may still be valid')
			})
			oidcClient.events.addUserLoaded(user => {
				oidcLog('info', 'User loaded event — refreshing Cognito credentials and cookie')

				// Refresh AWS Cognito credentials with the new token
				elasticSearchClient.refreshCognitoWithOidcIdToken(user)
					.then(() => {
						oidcLog('info', 'Cognito credentials refreshed successfully')
					})
					.catch(error => {
						oidcLog('error', 'Failed to refresh Cognito credentials', error)
					})

				// Update the cookie with the new id_token to keep them in sync
				let currentUser = window.vm.$cookies.get('user')
				if (currentUser && user.id_token) {
					currentUser.google_id_token = user.id_token
					window.vm.$store.commit('set_user', currentUser)
					// Refresh cookie expiry (24 hours to align better with token lifecycle)
					window.vm.$cookies.set('user', currentUser, 3600 * 24)
					oidcLog('info', 'User token refreshed, cookie updated (24h expiry)')
				}
			})

			// ---- Cross-tab token synchronization ----
			// The OIDC userStore uses localStorage (shared between tabs).
			// When another tab successfully renews the tokens, the 'storage'
			// event fires in all OTHER tabs. If this tab had previously
			// marked the session as invalidated (e.g., it lost the refresh
			// token race), we can recover by picking up the fresh tokens.
			window.addEventListener('storage', (event) => {
				if (!event.key || !event.key.startsWith('oidc.user:')) return
				if (!sessionInvalidated) return

				oidcLog('info', 'Storage event detected on key "' + event.key + '" — another tab may have renewed tokens')
				oidcClient.getUser().then(user => {
					if (user && !user.expired) {
						const remaining_seconds = user.expires_at - Math.floor(Date.now() / 1000)
						if (remaining_seconds > 120) {
							oidcLog('info', 'Fresh tokens from another tab (expires_at: ' + new Date(user.expires_at * 1000).toISOString() + ', remaining: ' + remaining_seconds + 's), clearing invalidation and restarting silent renew')
							clearSessionInvalidated()
							sessionExpiredToastId = null
							oidcClient.startSilentRenew()
						} else {
							oidcLog('info', 'Storage event but token only has ' + remaining_seconds + 's remaining — not a fresh renewal')
						}
					}
				}).catch(() => {
					// Ignore — we're already in an invalidated state
				})
			})

			context.commit('OIDC_LIBRARY_EVENTS_HANDLED')
		}
		context.commit('AUTHENTICATION_SUCCESS', user)

		let custom_user = {
			email: user.profile.email.toLowerCase(),
			name: user.profile.name,
			image: null,
			google_id_token: user.id_token,
			maestroAuth: null,
			maestroUserId: null
		}
		window.vm.$ApiMaestro.get_or_create_account(custom_user.email, custom_user.name)
			.then(result => {
				custom_user.maestroAuth = result.maestroAuth
				custom_user.maestroUserId = result.maestroUserId
				window.vm.$store.commit('set_user', custom_user)
				// Set cookie with 24 hour expiry (will be refreshed on token renewal)
				window.vm.$cookies.set('user', custom_user, 3600 * 24)
				let next_url = window.localStorage.getItem('next_url')
				if (next_url) {
					window.localStorage.removeItem('next_url')
				}
				router.push(next_url || '/')
					.catch((error) => {
						console.log(error.message)
					})
			})
			.catch(error => {
				oidcLog('error', 'Failed to get or create Maestro account', error)
				window.vm.$Toaster({
					type: 'error',
					title: 'Erreur d\'authentification',
					content: 'Impossible de créer ou récupérer votre compte Maestro. Veuillez réessayer.'
				})
				// Redirect to login page so user can retry
				router.push('/login')
					.catch((err) => {
						console.log(err.message)
					})
			})
	},
	signOut: (context) => {
		context.commit('LOGOUT_REQUEST_PENDING')
		// Clear local session data before redirecting to SSO logout
		window.vm.$cookies.remove('user')
		clearSessionInvalidated()
		sessionExpiredToastId = null
		return oidcClient.signoutRedirect()
			.then(() => {
				context.commit('LOGOUT_REQUEST_SUCCESS')
			})
			.catch(function (error) {
				context.commit('LOGOUT_REQUEST_FAILURE', error)
				throw error
			})
	},
	getUser: () => {
		return new Promise(resolve => {
			// Check if session was already invalidated (e.g., invalid_grant error)
			// to avoid attempting a signinSilent that will fail
			if (sessionInvalidated) {
				oidcLog('info', 'Session already invalidated, skipping getUser')
				resolve(null)
				return
			}

			oidcClient.getUser()
				.then(user => {
					if (!user) {
						oidcLog('info', 'No user session found')
						resolve(null)
						return
					}
					if (user.expired) {
						oidcLog('info', 'User session expired, attempting silent renew')
						return oidcClient.signinSilent()
					} else {
						return user
					}
				})
				.then(user => {
					if (!user) {
						resolve(null)
						return
					}
					resolve(user)
				})
				.catch((err) => {
					oidcLog('warn', 'getUser error', err)
					const errorMessage = err && err.message ? err.message.toLowerCase() : ''
					const isAuthError = errorMessage.includes('invalid_grant') ||
						errorMessage.includes('login_required') ||
						errorMessage.includes('consent_required') ||
						errorMessage.includes('interaction_required')

					if (isAuthError) {
						// Multi-tab recovery: before giving up, check if another tab
						// already renewed the tokens (race condition scenario).
						// We re-read from localStorage to see if fresh tokens are available.
						oidcClient.getUser().then(freshUser => {
							if (freshUser && !freshUser.expired) {
								const remaining_seconds = freshUser.expires_at - Math.floor(Date.now() / 1000)
								if (remaining_seconds > 120) {
									oidcLog('info', 'getUser: Fresh tokens found after signinSilent failure (remaining: ' + remaining_seconds + 's), multi-tab recovery')
									clearSessionInvalidated()
									oidcClient.startSilentRenew()
									resolve(freshUser)
									return
								}
							}
							// No recovery possible
							markSessionInvalidated()
							resolve(null)
						}).catch(() => {
							markSessionInvalidated()
							resolve(null)
						})
					} else {
						resolve(null)
					}
				})
		})
	},
	checkAccess: (context, requiresAuth) => {
		return new Promise(resolve => {
			oidcClient = getOidcClient()
			if (!requiresAuth) return resolve('OK')

			// If session was invalidated (invalid_grant, etc.), don't attempt
			// to restore authentication — it will fail and could restart
			// silent renew via setAuthenticationSuccess, undoing the invalidation.
			if (sessionInvalidated) {
				oidcLog('info', 'Session invalidated, checkAccess returning UNAUTHORIZED')
				resolve('UNAUTHORIZED')
				return
			}

			// Check if we have a token in Session Storage
			const getUserPromise = new Promise(resolve => {
				oidcClient.getUser().then(user => {
					resolve(user)
				}).catch(() => {
					resolve(null)
				})
			})
			let statusCode = 'UNAUTHORIZED'
			getUserPromise.then(user => {
				// Check if we have token information and if token is not expired
				if (user && !user.expired) {
					statusCode = 'OK'
					context.dispatch('setAuthenticationSuccess', user)
				}
				resolve(statusCode)
			})
		})
	}
}

const mutations = {
	TOKEN_REQUEST_PENDING: (state) => {
		state.user = { loading: true }
	},
	TOKEN_REQUEST_SUCCESS: (state) => {
		state.user = { loading: false }
	},
	TOKEN_REQUEST_FAILURE: (state, error) => {
		state.user = { error }
	},
	LOGOUT_REQUEST_PENDING: (state) => {
		state.user = { loading: true }
	},
	LOGOUT_REQUEST_SUCCESS: (state) => {
		state.user = {}
	},
	LOGOUT_REQUEST_FAILURE: (state, error) => {
		state.user = { error }
	},
	AUTHENTICATION_SUCCESS: (state, user) => {
		state.user = user
	},
	OIDC_LIBRARY_EVENTS_HANDLED: (state) => {
		state.isOidcEventsHandled = true
	}
}

const getters = {
	accessToken (state) {
		return state.user.access_token
	},
	isLoading (state) {
		return (state.user != null && state.user.isLoading)
	},
	tokenResponse (state) {
		return (state.user) ? state.user : {}
	},
	userProfile (state) {
		return (state.user && state.user.profile != null) ? state.user.profile : {}
	},
	userIsAuthenticated (state) {
		return (state.user && state.user.id_token != null)
	}
}

export default {
	namespaced: true,
	state: defaultState,
	getters,
	actions,
	mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/auth.js